'use client'

/**
 * Шаг 2 импорта — маппинг колонок (Intelligence Terminal §Forms, §Tables).
 */

import { useState } from 'react'
import { IMPORT_TARGET_FIELDS, IMPORT_TARGET_COLUMNS } from '../import-wizard.schema'
import { ArrowRight, ArrowLeft, Check, Warning, ArrowDown, ArrowUp } from '@phosphor-icons/react'
import type { ColumnMatch } from '../import-wizard.types'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'

interface FileUploadInfo {
  headers: string[]
  sampleRows: Record<string, unknown>[]
  totalRows: number
  columnMatches: ColumnMatch[]
}

interface Props {
  importFile: FileUploadInfo | null
  exportFile: FileUploadInfo | null
  importMappingConfig: Record<string, string>
  exportMappingConfig: Record<string, string>
  headers: string[]
  columnMatches: ColumnMatch[]
  mappingConfig: Record<string, string>
  sampleRows: Record<string, unknown>[]
  onSetMapping: (source: string, target: string, fileType?: 'import' | 'export') => void
  onNext: () => void
  onBack: () => void
  loading: boolean
}

type FileTab = 'import' | 'export'

export function StepMapping({
  importFile, exportFile,
  importMappingConfig, exportMappingConfig,
  headers: legacyHeaders, columnMatches: legacyMatches,
  mappingConfig: legacyMappingConfig, sampleRows: legacySampleRows,
  onSetMapping, onNext, onBack, loading,
}: Props) {
  const t = useT()
  const isTwoFileMode = importFile !== null && exportFile !== null
  const [activeTab, setActiveTab] = useState<FileTab>('import')

  const targetFields = isTwoFileMode ? IMPORT_TARGET_FIELDS : IMPORT_TARGET_COLUMNS

  const currentHeaders = isTwoFileMode
    ? (activeTab === 'import' ? importFile!.headers : exportFile!.headers)
    : legacyHeaders

  const currentMatches = isTwoFileMode
    ? (activeTab === 'import' ? importFile!.columnMatches : exportFile!.columnMatches)
    : legacyMatches

  const currentMapping = isTwoFileMode
    ? (activeTab === 'import' ? importMappingConfig : exportMappingConfig)
    : legacyMappingConfig

  const currentSampleRows = isTwoFileMode
    ? (activeTab === 'import' ? importFile!.sampleRows : exportFile!.sampleRows)
    : legacySampleRows

  const requiredFields = targetFields.filter((c) => c.required).map((c) => c.key)
  const allMappedValues = isTwoFileMode
    ? [...Object.values(importMappingConfig), ...Object.values(exportMappingConfig)]
    : Object.values(legacyMappingConfig)
  const mappedRequired = requiredFields.filter((f) => allMappedValues.includes(f))
  const allRequiredMapped = mappedRequired.length === requiredFields.length

  const handleSetMapping = (source: string, target: string) => {
    if (isTwoFileMode) {
      onSetMapping(source, target, activeTab)
    } else {
      onSetMapping(source, target)
    }
  }

  return (
    <div className="space-y-4">
      {/* Tab switcher */}
      {isTwoFileMode && (
        <div className="flex flex-wrap gap-2">
          <TabButton
            active={activeTab === 'import'}
            onClick={() => setActiveTab('import')}
            icon={<ArrowDown size={12} />}
          >
            {t('importWizard.mapping.importTab', { rows: importFile!.totalRows, cols: importFile!.headers.length })}
          </TabButton>
          <TabButton
            active={activeTab === 'export'}
            onClick={() => setActiveTab('export')}
            icon={<ArrowUp size={12} />}
          >
            {t('importWizard.mapping.exportTab', { rows: exportFile!.totalRows, cols: exportFile!.headers.length })}
          </TabButton>
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] text-[var(--text-secondary)]">
            {isTwoFileMode
              ? t('importWizard.mapping.hintTab', {
                  side:
                    activeTab === 'import'
                      ? t('importWizard.mapping.sideImport')
                      : t('importWizard.mapping.sideExport'),
                })
              : t('importWizard.mapping.hintSingle')}
          </p>
          <span className="font-mono text-[11px] tabular-nums text-[var(--text-tertiary)]">
            {t('importWizard.mapping.required')}{' '}
            <span
              className={cn(
                'font-medium',
                allRequiredMapped ? 'text-risk-low' : 'text-risk-high',
              )}
            >
              {mappedRequired.length}/{requiredFields.length}
            </span>
          </span>
        </div>

        <div
          className={cn(
            'rounded-[4px] border px-4 py-3 text-[12px]',
            allRequiredMapped
              ? 'bg-risk-low-bg border-risk-low text-risk-low'
              : 'bg-risk-medium-bg border-risk-medium text-risk-medium',
          )}
        >
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.08em]">
            {allRequiredMapped
              ? t('importWizard.mapping.allMapped')
              : t('importWizard.mapping.needMapping')}
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {targetFields.filter((f) => f.required).map((field) => {
              const isMapped = allMappedValues.includes(field.key)
              return (
                <span
                  key={field.key}
                  className={cn(
                    'inline-flex items-center gap-1 text-[11px]',
                    isMapped
                      ? 'text-risk-low font-normal'
                      : 'text-risk-high font-medium',
                  )}
                >
                  {isMapped ? <Check size={10} weight="bold" /> : <Warning size={10} weight="bold" />}
                  {field.label}
                </span>
              )
            })}
          </div>
        </div>
      </div>

      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {currentHeaders.map((header) => {
          const match = currentMatches.find((m) => m.sourceColumn === header)
          const currentTarget = currentMapping[header] || ''
          const sample = currentSampleRows[0]?.[header]

          return (
            <div
              key={header}
              className="flex items-center gap-3 rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-[var(--text-primary)]">{header}</p>
                <p className="truncate font-mono text-[10px] tabular-nums text-[var(--text-tertiary)]">
                  {t('importWizard.mapping.sample', { value: sample !== undefined ? String(sample) : '—' })}
                </p>
              </div>
              <ArrowRight size={12} className="shrink-0 text-[var(--text-tertiary)]" />
              <div className="w-56">
                <select
                  value={currentTarget}
                  onChange={(e) => handleSetMapping(header, e.target.value)}
                  className={cn(
                    'block w-full h-9 rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-canvas)] px-2 text-[12px] text-[var(--text-primary)]',
                    'focus-visible:outline-none focus-visible:border-[var(--accent-data)] focus-visible:ring-2 focus-visible:ring-[var(--accent-data)]/30',
                  )}
                >
                  <option value="">{t('importWizard.mapping.skip')}</option>
                  {targetFields.map((col) => (
                    <option
                      key={col.key}
                      value={col.key}
                      disabled={Object.values(currentMapping).includes(col.key) && currentMapping[header] !== col.key}
                    >
                      {col.label} {col.required ? '(*)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="w-5 shrink-0">
                {match?.status === 'auto' && currentTarget ? (
                  <Check size={12} className="text-risk-low" />
                ) : currentTarget ? (
                  <Check size={12} className="text-accent-data" />
                ) : match?.confidence && match.confidence > 0.5 ? (
                  <Warning size={12} className="text-risk-medium" />
                ) : null}
              </div>
            </div>
          )
        })}
      </div>

      {isTwoFileMode && (
        <div className="flex flex-wrap gap-4 border-t border-[var(--border-subtle)] pt-2 font-mono text-[11px] tabular-nums text-[var(--text-tertiary)]">
          <span>
            {t('importWizard.mapping.importSummary', { mapped: Object.keys(importMappingConfig).length })}{' '}
            {t('importWizard.mapping.ofColumns', { total: importFile!.headers.length })}
          </span>
          <span>
            {t('importWizard.mapping.exportSummary', { mapped: Object.keys(exportMappingConfig).length })}{' '}
            {t('importWizard.mapping.ofColumns', { total: exportFile!.headers.length })}
          </span>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-[var(--border-subtle)] pt-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-[4px] border border-[var(--border-default)] bg-transparent px-3 py-1.5 text-[12px] font-medium text-[var(--text-secondary)] transition-colors duration-150 hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]"
        >
          <ArrowLeft size={12} />
          {t('common.action.back')}
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!allRequiredMapped || loading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[12px] font-medium',
            'bg-accent-data text-fg-on-risk',
            'transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-data)] focus-visible:ring-offset-2',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          {loading ? t('importWizard.mapping.validating') : t('importWizard.mapping.submit')}
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[2px] border px-3 py-1.5 text-[12px] font-medium',
        'transition-colors duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-data)] focus-visible:ring-offset-2',
        active
          ? 'border-[var(--text-primary)] bg-[var(--bg-canvas)] text-[var(--text-primary)]'
          : 'border-[var(--border-default)] bg-transparent text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]',
      )}
    >
      {icon}
      {children}
    </button>
  )
}
